// src/pagesMVP1010/ResetPasswordConfirmPage.jsx
import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../assets/styles/UpdatedWelcomePage.css';
import Blossom from '../assets/images/Blossom.webp';
import BloomPal_Logo from '../assets/images/BloomPal_MainLogo.png';
import api from '../utils/api';

const ResetPasswordConfirmPage = () => {
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const ensureCsrf = async () => {
    try {
      await api.get('/api/set-csrf/');
    } catch (e) {
      // CSRF setup failed, but continue anyway
    }
  };

  const pickError = (e, fallback) => {
    return (
      e?.response?.data?.new_password1?.[0] ||
      e?.response?.data?.new_password2?.[0] ||
      e?.response?.data?.non_field_errors?.[0] ||
      e?.response?.data?.token?.[0] ||
      e?.response?.data?.uid?.[0] ||
      e?.response?.data?.detail ||
      e?.response?.data?.error ||
      e?.message ||
      fallback
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate passwords match
    if (newPassword1 !== newPassword2) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    // Validate password length (minimum 8 characters is common)
    if (newPassword1.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setBusy(true);

    try {
      await ensureCsrf();
      
      // URL decode uid and token in case they were URL-encoded
      const decodedUid = uid ? decodeURIComponent(uid) : uid;
      const decodedToken = token ? decodeURIComponent(token) : token;
      
      await api.post('/api/auth/password/reset/confirm/', {
        uid: decodedUid,
        token: decodedToken,
        new_password1: newPassword1,
        new_password2: newPassword2,
      });
      setSuccess(true);
      setNewPassword1('');
      setNewPassword2('');
    } catch (err) {
      setError(pickError(err, 'Failed to reset password. The link may have expired. Please request a new one.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wp-root">
      {/* Logo */}
      <div className="wp-logo-wrap">
        <img 
          src={BloomPal_Logo} 
          alt="BloomPal" 
          className="wp-logo" 
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
      </div>

      {/* Decorative bubbles */}
      <div className="wp-bubbles">
        <span className="b b1" />
        <span className="b b2" />
        <span className="b b3" />
        <span className="b b4" />
        <span className="b b5" />
      </div>

      <div className="wp-grid">
        {/* LEFT SIDE - Mascot and text */}
        <div className="wp-left">
          <img src={Blossom} alt="Blossom" className="wp-mascot" />
          <h1 className="wp-h1">Almost there! Pick a new secure password.</h1>
        </div>

        {/* RIGHT SIDE - Reset password confirmation card */}
        <div className="wp-right">
          <div className="wp-card">
            <h2 className="wp-card-title">Set New Password</h2>
            <p style={{ 
              textAlign: 'center', 
              color: '#60727e', 
              fontSize: '14px', 
              lineHeight: '1.5',
              margin: '0 0 24px 0' 
            }}>
              Enter your new password below. Make sure it's at least 8 characters long.
            </p>

            {success && (
              <div style={{
                background: '#e8f5e9',
                color: '#2e7d32',
                border: '1px solid #a5d6a7',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '16px',
                textAlign: 'center',
                fontWeight: '600'
              }} role="alert">
                Password Changed!
              </div>
            )}

            {error && (
              <div style={{
                background: '#ffeaea',
                color: '#8a1f1f',
                border: '1px solid #f6b8b8',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '16px'
              }} role="alert">
                {error}
              </div>
            )}

            {!success ? (
              <form className="wp-form" onSubmit={handleSubmit}>
                <input
                  type="password"
                  className="wp-input"
                  placeholder="New Password"
                  value={newPassword1}
                  onChange={(e) => setNewPassword1(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={busy}
                  style={{ marginBottom: '12px' }}
                />

                <input
                  type="password"
                  className="wp-input"
                  placeholder="Confirm New Password"
                  value={newPassword2}
                  onChange={(e) => setNewPassword2(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={busy}
                  style={{ marginBottom: '16px' }}
                />

                <button 
                  type="submit" 
                  className="wp-cta"
                  disabled={busy || !newPassword1 || !newPassword2}
                >
                  {busy ? 'Resetting...' : 'Reset Password'}
                </button>

                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '20px',
                  fontSize: '14px'
                }}>
                  <Link 
                    to="/" 
                    style={{ 
                      color: '#7a8ea0', 
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    ← Back to Login
                  </Link>
                </div>
              </form>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <button 
                  onClick={() => navigate('/welcome')}
                  className="wp-cta"
                  style={{ marginTop: '16px' }}
                >
                  Log In Now
                </button>
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '20px',
                  fontSize: '14px'
                }}>
                  <Link 
                    to="/" 
                    style={{ 
                      color: '#7a8ea0', 
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    ← Back to Home
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirmPage;

